// @flow
import type { FSA } from 'types/redux';
import type {
  EventData,
  DriverEvent,
  DriverLoggedInEvent,
  DriverLocationUpdateEvent,
  PassengerEvent,
  PassengerLoggedInEvent,
  BookingPassengerEvent,
  BookingDriverEvent,
  DriverAvailabilityUpdateEvent,
  DriverAutoAcceptUpdateEvent,
  ResourceCreationStatusEvent,
} from 'types/events';
import type {
  Driver,
  Location,
  Passenger,
} from 'types/entities';
import type { Booking } from 'types/bookings';

import {
  BOOKING_STATE_BID,
  BOOKING_STATE_DECLINED,
  BOOKING_STATE_PICKING_UP,
  BOOKING_STATE_DROPPING_OFF,
  BOOKING_STATE_COMPLETED,
  BOOKING_STATE_BID_CANCELLED,
  BOOKING_STATE_JOB_CANCELLED,
  BOOKING_STATE_UNALLOCATED,
  BOOKING_STATE_IGNORED,
} from 'types/bookings';
import {
  COHORT_DELETED_EVENT,
  DRIVER_NEW_EVENT,
  DRIVER_LOGGED_IN_EVENT,
  DRIVER_LOGGED_OUT_EVENT,
  DRIVER_LOCATION_UPDATE_EVENT,
  DRIVER_DISCONNECTED_EVENT,
  DRIVER_DELETED_EVENT,
  PASSENGER_NEW_EVENT,
  PASSENGER_LOGGED_IN_EVENT,
  PASSENGER_LOGGED_OUT_EVENT,
  PASSENGER_DELETED_EVENT,
  BOOKING_DRIVER_NEW_EVENT,
  BOOKING_DRIVER_BID_EVENT,
  BOOKING_DRIVER_DECLINED_EVENT,
  BOOKING_DRIVER_AWARDED_EVENT,
  BOOKING_DRIVER_PICKED_UP_EVENT,
  BOOKING_DRIVER_DROPPED_OFF_EVENT,
  BOOKING_DRIVER_COMPLETE_EVENT,
  BOOKING_DRIVER_CANCELLED_EVENT,
  BOOKING_DRIVER_RECEIVED_CANCELLATION_EVENT,
  BOOKING_DRIVER_IGNORED_EVENT,
  BOOKING_PASSENGER_NEW_EVENT,
  BOOKING_PASSENGER_UNALLOCATED_EVENT,
  BOOKING_PASSENGER_AWARDED_EVENT,
  BOOKING_PASSENGER_PICKED_UP_EVENT,
  BOOKING_PASSENGER_COMPLETED_EVENT,
  BOOKING_PASSENGER_CANCELLED_EVENT,
  BOOKING_PASSENGER_RECEIVED_CANCELLATION_EVENT,
  DRIVER_AVAILABILITY_UPDATE_EVENT,
  DRIVER_AUTO_ACCEPT_UPDATE_EVENT,
  RESOURCE_CREATION_STATUS_EVENT,
} from 'types/events';

import {
  cohortDeleteInstant,
  cohortDriverAssociateInstant,
  cohortPassengerAssociateInstant,
  getCohorStatus,
} from 'actions/cohorts';
import {
  driverLoginInstant,
  driverLogoutInstant,
  driverSetLocationInstant,
  driverSetAvailabilityInstant,
  driverSetAutoAcceptInstant,
  driverDeleteInstant,
  driverBookingNew,
  driverBookingStateChange,
} from 'actions/drivers';
import {
  passengerLoginInstant,
  passengerLogoutInstant,
  passengerDeleteInstant,
  passengerBookingNew,
  passengerBookingStateChange,
} from 'actions/passengers';

export const emptyEvent: FSA = {
  type: '',
  payload: null,
};

export default function handleCohortEvent(cohortID: string, eventData: EventData): FSA {
  const { type, params } = eventData;
  switch (type) {
    case COHORT_DELETED_EVENT:
      {
        return cohortDeleteInstant(cohortID);
      }
    case DRIVER_NEW_EVENT:
      {
        const driver: Driver = params;
        return cohortDriverAssociateInstant(cohortID, driver.driverID, driver);
      }
    case DRIVER_LOGGED_IN_EVENT:
      {
        const event: DriverLoggedInEvent = params;
        const { driverID, behaviour } = event;
        return driverLoginInstant(cohortID, driverID, behaviour);
      }
    case DRIVER_LOGGED_OUT_EVENT:
    case DRIVER_DISCONNECTED_EVENT:
      {
        const event: DriverEvent = params;
        const { driverID } = event;
        return driverLogoutInstant(cohortID, driverID);
      }
    case DRIVER_LOCATION_UPDATE_EVENT:
      {
        const event: DriverLocationUpdateEvent = params;
        const { driverID, latitude, longitude } = event;
        const location: Location = [longitude, latitude];
        return driverSetLocationInstant(cohortID, driverID, location, false);
      }
    case DRIVER_DELETED_EVENT:
      {
        const event: DriverEvent = params;
        const { driverID } = event;
        return driverDeleteInstant(cohortID, driverID);
      }
    case PASSENGER_NEW_EVENT:
      {
        const passenger: Passenger = params;
        return cohortPassengerAssociateInstant(cohortID, passenger.passengerID, passenger);
      }
    case PASSENGER_LOGGED_IN_EVENT:
      {
        const event: PassengerLoggedInEvent = params;
        const { passengerID, behaviour } = event;
        return passengerLoginInstant(cohortID, passengerID, behaviour);
      }
    case PASSENGER_LOGGED_OUT_EVENT:
      {
        const event: PassengerEvent = params;
        const { passengerID } = event;
        return passengerLogoutInstant(cohortID, passengerID);
      }
    case PASSENGER_DELETED_EVENT:
      {
        const event: PassengerEvent = params;
        const { passengerID } = event;
        return passengerDeleteInstant(cohortID, passengerID);
      }
    case BOOKING_DRIVER_NEW_EVENT:
      {
        const event: BookingDriverEvent = params;
        const booking: Booking = params;
        const { driverID, bookingID } = event;
        return driverBookingNew(cohortID, driverID, bookingID, booking);
      }
    case BOOKING_DRIVER_BID_EVENT:
    case BOOKING_DRIVER_DECLINED_EVENT:
    case BOOKING_DRIVER_AWARDED_EVENT:
    case BOOKING_DRIVER_PICKED_UP_EVENT:
    case BOOKING_DRIVER_DROPPED_OFF_EVENT:
    case BOOKING_DRIVER_COMPLETE_EVENT:
    case BOOKING_DRIVER_CANCELLED_EVENT:
    case BOOKING_DRIVER_RECEIVED_CANCELLATION_EVENT:
    case BOOKING_DRIVER_IGNORED_EVENT:
      {
        const bookingDriverEventMap = {
          // Map the event to the next state of a booking.
          [BOOKING_DRIVER_BID_EVENT]: BOOKING_STATE_BID,
          [BOOKING_DRIVER_DECLINED_EVENT]: BOOKING_STATE_DECLINED,
          [BOOKING_DRIVER_AWARDED_EVENT]: BOOKING_STATE_PICKING_UP,
          [BOOKING_DRIVER_PICKED_UP_EVENT]: BOOKING_STATE_DROPPING_OFF,
          [BOOKING_DRIVER_DROPPED_OFF_EVENT]: BOOKING_STATE_DROPPING_OFF,
          [BOOKING_DRIVER_COMPLETE_EVENT]: BOOKING_STATE_COMPLETED,
          [BOOKING_DRIVER_CANCELLED_EVENT]: BOOKING_STATE_BID_CANCELLED,
          [BOOKING_DRIVER_RECEIVED_CANCELLATION_EVENT]: BOOKING_STATE_JOB_CANCELLED,
          [BOOKING_DRIVER_IGNORED_EVENT]: BOOKING_STATE_IGNORED,
        };
        const event: BookingDriverEvent = params;
        const { driverID, bookingID } = event;
        return driverBookingStateChange(cohortID, driverID, bookingID, bookingDriverEventMap[type]);
      }
    case BOOKING_PASSENGER_NEW_EVENT:
      {
        const event: BookingPassengerEvent = params;
        const booking: Booking = params;
        const { passengerID, bookingID } = event;
        return passengerBookingNew(cohortID, passengerID, bookingID, booking);
      }
    case BOOKING_PASSENGER_UNALLOCATED_EVENT:
    case BOOKING_PASSENGER_AWARDED_EVENT:
    case BOOKING_PASSENGER_PICKED_UP_EVENT:
    case BOOKING_PASSENGER_COMPLETED_EVENT:
    case BOOKING_PASSENGER_CANCELLED_EVENT:
    case BOOKING_PASSENGER_RECEIVED_CANCELLATION_EVENT:
      {
        const bookingPassengerEventMap = {
          [BOOKING_PASSENGER_UNALLOCATED_EVENT]: BOOKING_STATE_UNALLOCATED,
          [BOOKING_PASSENGER_AWARDED_EVENT]: BOOKING_STATE_PICKING_UP,
          [BOOKING_PASSENGER_PICKED_UP_EVENT]: BOOKING_STATE_DROPPING_OFF,
          [BOOKING_PASSENGER_COMPLETED_EVENT]: BOOKING_STATE_COMPLETED,
          [BOOKING_PASSENGER_CANCELLED_EVENT]: BOOKING_STATE_JOB_CANCELLED,
          [BOOKING_PASSENGER_RECEIVED_CANCELLATION_EVENT]:
            BOOKING_STATE_JOB_CANCELLED,
        };
        const event: BookingPassengerEvent = params;
        const { passengerID, bookingID } = event;
        return passengerBookingStateChange(cohortID, passengerID, bookingID,
          bookingPassengerEventMap[type]);
      }
    case DRIVER_AVAILABILITY_UPDATE_EVENT:
      {
        const event: DriverAvailabilityUpdateEvent = params;
        const { driverID, availability } = event;
        return driverSetAvailabilityInstant(cohortID, driverID, availability);
      }
    case DRIVER_AUTO_ACCEPT_UPDATE_EVENT:
      {
        const event: DriverAutoAcceptUpdateEvent = params;
        const { driverID, isTurnedOn } = event;
        return driverSetAutoAcceptInstant(cohortID, driverID, isTurnedOn);
      }
    case RESOURCE_CREATION_STATUS_EVENT:
      {
        const event: ResourceCreationStatusEvent = params;
        const { status, error } = event;
        return getCohorStatus(cohortID, status, error);
      }
    default:
      break;
  }
  return emptyEvent;
}



/** WEBPACK FOOTER **
 ** ./src/js/views/dashboard/cohort-events-handler.js
 **/